import { FunctionComponent, useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import styled, { css } from 'styled-components'

import config from '@config'
import { desktopCSS } from '@measures/responsive'
import translate from '@i18n'
import usePageMetadata from '@hooks/usePageMetadata'
import useShouldShowTiunSnippet from '@hooks/useShouldShowTiunSnippet'
import useTiunSession from '@hooks/useTiunSession'
import useTiunTrackingEvents from '@hooks/useTiunTrackingEvents'
import useViewportTracking from '@hooks/useViewportTracking'
import Blink from '@components/Blink'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'
import { updateTiunSnippet } from '@components/Tiun/utils'

const {
  tiun: { faqUrl },
} = config

const Wrapper = styled.div`
  ${({
    theme: {
      colors: { fillSurface },
      spacing: { spacing24 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    box-sizing: border-box;
    gap: 30px;
    padding: 36px ${spacing24};
    ${desktopCSS(css`
      padding: 69px;
      gap: 48px;
    `)}

    width: 100%;
    bottom: 0;
    z-index: 2;
    background-color: ${fillSurface};
  `}
`

const StyledTitle = styled.h1`
  ${({
    theme: {
      typography: {
        headings: {
          xxlarge: { bundledCSS: xxlargeHeadingsCSS },
          medium: { bundledCSS: mediumHeadingsCSS },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${mediumHeadingsCSS};
    margin: 0;
    ${desktopCSS(css`
      ${xxlargeHeadingsCSS};
    `)}
    color: ${textDefault}
  `}
`
const StyledSubTitle = styled.span`
  ${({
    theme: {
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadingsCSS },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${small1SubheadingsCSS};
    color: ${textDefault};
  `}
`
const StyledTitleWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${spacing16};
  `}
`
const StyledLink = styled(Blink)`
  ${({
    theme: {
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadingsCSS },
        },
      },
      colors: { textBrand },
      spacing: { spacing4 },
    },
  }) => css`
    ${small1SubheadingsCSS};
    color: ${textBrand};
    margin-left: ${spacing4};
  `}
`

const StyledTiunSnippet = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    ${desktopCSS(css`
      margin-top: ${spacing16};
    `)}
    margin-top: 10px;
  `}
`

const TiunSnippet: FunctionComponent = () => {
  const { isPlus } = usePageMetadata()
  const { isTiunSessionActive } = useTiunSession()
  const { onTiunSnippetShow, onTiunClick, onPianoShow } =
    useTiunTrackingEvents()
  const shouldShowTiunSnippet = useShouldShowTiunSnippet()
  const queryClient = useQueryClient()

  const handleCTAClick = useCallback(() => {
    onTiunClick('paywall')
    onPianoShow()
    updateTiunSnippet(queryClient, false)
  }, [queryClient, onTiunClick, onPianoShow])

  const viewportRef = useViewportTracking({
    onImpression: onTiunSnippetShow,
    track: true,
  })

  if (!isPlus || isTiunSessionActive || !shouldShowTiunSnippet) {
    return null
  }

  return (
    <Wrapper ref={viewportRef}>
      <StyledTitleWrapper>
        <StyledTitle>{translate('tiun.title')}</StyledTitle>
        <StyledSubTitle>
          {translate('tiun.text')}
          <StyledLink href={faqUrl} target="_blank">
            {translate('tiun.link')}
          </StyledLink>
        </StyledSubTitle>
      </StyledTitleWrapper>
      <StyledTiunSnippet id="tiunPaymentSnippet" />
      <SecondaryCTAButton onClick={handleCTAClick} colorVariant="black">
        {translate('tiun.buttonText')}
      </SecondaryCTAButton>
    </Wrapper>
  )
}

export default TiunSnippet
